import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { AlertTriangle, ArrowLeft, Loader2, XCircle } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { PlanBadge } from '../../components/ui/Badge'
import useAuthStore from '../../store/authStore'
import useOrgStore from '../../store/orgStore'
import { stripeApi } from '../../lib/api'
import { formatDate } from '../../lib/utils'
import {
  BILLING_PORTAL_CANCELLATION_NOTE,
  formatBillingPlanSummary,
  formatScheduledCancellationMessage,
  getCancelAtPeriodEnd,
  getCurrentPeriodEnd,
  getSubscriptionPlan,
  getSubscriptionStatus,
} from '../../lib/subscription'
import toast from 'react-hot-toast'

const CANCELABLE_STATUSES = ['active', 'trialing', 'past_due']
const PAID_PLANS = ['starter', 'pro', 'business', 'enterprise']

export function CancelSubscription() {
  const navigate = useNavigate()
  const { user, loadUser } = useAuthStore()
  const { org, usage, fetchOrg, fetchUsage } = useOrgStore()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchOrg()
    fetchUsage()
  }, [fetchOrg, fetchUsage])

  const subscriptionPlan = getSubscriptionPlan({ user, org, usage })
  const subscriptionStatus = getSubscriptionStatus({ user, org, usage })
  const currentPeriodEnd = getCurrentPeriodEnd({ user, org, usage })
  const cancelAtPeriodEnd = getCancelAtPeriodEnd({ user, org, usage })
  const billingPlanSummary = formatBillingPlanSummary(subscriptionPlan, {
    cancelAtPeriodEnd,
    currentPeriodEnd,
  })
  const scheduledCancellationMessage = formatScheduledCancellationMessage(
    subscriptionPlan,
    currentPeriodEnd
  )
  const isOwner = user?.role === 'owner'
  const canCancel =
    isOwner &&
    PAID_PLANS.includes(subscriptionPlan) &&
    CANCELABLE_STATUSES.includes(subscriptionStatus || '')

  async function handleCancelSubscription() {
    setLoading(true)
    try {
      await stripeApi.cancelSubscription()
      await Promise.allSettled([fetchOrg(), fetchUsage(), loadUser()])
      navigate('/dashboard/settings?tab=billing&subscription_cancelled=true')
    } catch (error) {
      toast.error(error.message || 'Failed to cancel subscription')
    } finally {
      setLoading(false)
    }
  }

  async function handleBackToBilling() {
    await Promise.allSettled([fetchOrg(), fetchUsage(), loadUser()])
    navigate('/dashboard/settings?tab=billing')
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-8"
      >
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 rounded-2xl bg-red-100 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-7 h-7 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Cancel subscription</h1>
            <p className="text-sm text-gray-500 mt-1">
              Cancellation takes effect at the end of your current billing period. Your paid access stays active until then.
            </p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-2xl p-5 mb-6">
          <p className="text-sm text-gray-500 mb-2">Current billing plan</p>
          <div className="flex items-center gap-3">
            <PlanBadge plan={subscriptionPlan} size="lg" />
            <span className="text-sm text-gray-600">
              {billingPlanSummary}
            </span>
          </div>
        </div>

        {cancelAtPeriodEnd ? (
          <>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 text-sm text-amber-900">
              <p className="font-medium">{scheduledCancellationMessage}</p>
              <p className="mt-2 text-amber-800">
                {BILLING_PORTAL_CANCELLATION_NOTE}
              </p>
            </div>
            <Button
              variant="secondary"
              onClick={handleBackToBilling}
              icon={<ArrowLeft className="w-4 h-4" />}
            >
              Back to billing
            </Button>
          </>
        ) : canCancel ? (
          <>
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6 text-sm text-red-900">
              {currentPeriodEnd
                ? `If you cancel now, your ${billingPlanSummary.replace(/ \u2014.*$/, '')} stays active until ${formatDate(currentPeriodEnd)} and then cancels automatically.`
                : 'Your current paid plan stays active until the end of the billing period if you cancel now.'}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                variant="danger"
                loading={loading}
                onClick={handleCancelSubscription}
                icon={loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
              >
                Cancel at period end
              </Button>
              <Button
                variant="secondary"
                onClick={handleBackToBilling}
                icon={<ArrowLeft className="w-4 h-4" />}
              >
                Back to billing
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 mb-6 text-sm text-yellow-900">
              No active paid subscription is currently available to manage from this account.
            </div>
            <Button
              variant="secondary"
              onClick={handleBackToBilling}
              icon={<ArrowLeft className="w-4 h-4" />}
            >
              Back to billing
            </Button>
          </>
        )}
      </motion.div>
    </div>
  )
}
